import React from 'react';
import {
  Gift, CreditCard, RotateCcw, MessageSquare, Sparkles, Code, Search,
  Image as ImageIcon, Video, Music, Mic, Upload, Download, Bot, User, Coins,
} from 'lucide-react';
import type { LedgerEntry } from '../../services/walletService';
import { formatInstant } from '../../utils/datetime';

// Friendly OUTCOME labels for EVERY capability — never the provider/model name (Seedream/Seedance/Kling/Hailuo/…).
// A ledger row must show a human outcome, so the map is COMPLETE + the fallback is a generic word, never the raw key.
const CAPABILITY_LABEL: Record<string, string> = {
  casual: 'Chat', smart: 'Smart reply', direct: 'Shot directing', code: 'Coding', research: 'Research',
  image: 'Image', image_xl: 'Premium image', image_edit: 'Photo edit', image_seedream: 'Photoreal image',
  image_recraft: 'Illustration', image_portrait: 'Portrait',
  image_fill: 'Photo fill', image_seedream_lite: 'Photoreal image', media_prompt: 'Story planning',
  video: 'Video', video_pro: 'Cinematic video', video_seedance: 'Video', video_seedance2: 'Signature video',
  video_seedance2_fast: 'Signature video', video_hailuo: 'Video', video_pixverse: 'Video', video_animate: 'Animated clip',
  video_ltx: 'Video', video_veo3_fast: 'Spoken video', video_kling3: 'Cinematic video',
  video_seedance25: 'Signature video', video_veo31: 'Premium video',
  music: 'Music', music_instrumental: 'Instrumental', music_electronic: 'Music', sfx: 'Sound effect',
  audio: 'Voice', voice_premium: 'Voice', gallery_unlock: 'Gallery unlock',
  import: 'File import', export: 'Export',
};
/** A friendly label for a charge — never the raw capability key (which can reveal a model). Generic fallback by currency. */
function capabilityLabel(capability?: string | null, currency?: string | null): string {
  if (capability && CAPABILITY_LABEL[capability]) return CAPABILITY_LABEL[capability];
  return currency === 'MP' ? 'Media' : 'Usage'; // NEVER echo an unknown raw key (it may be a model name)
}

/**
 * The unit a row is labelled with. A row is displayed in the currency it was WRITTEN in: WP today,
 * SP or MP for rows that predate the merge. Restating an old MP amount as WP would halve its
 * apparent value, so the stored label always wins; only a row with no currency at all falls back.
 */
const unitOf = (e: LedgerEntry): string => e.currency ?? 'WP';

/** A distinct, representative icon PER action — chat, image, video, music, etc. — not one generic glyph. */
function iconFor(e: LedgerEntry): React.FC<{ className?: string }> {
  if (e.type === 'welcome' || e.type === 'grant') return Gift;
  if (e.type === 'topup') return CreditCard;
  if (e.type === 'refund') return RotateCcw;
  switch (e.capability) {
    case 'casual': return MessageSquare;
    case 'smart': return Sparkles;
    case 'code': return Code;
    case 'research': return Search;
    case 'image': return ImageIcon;
    case 'video': return Video;
    case 'music': case 'audio': return Music;
    case 'voice': return Mic;
    case 'import': return Upload;
    case 'export': return Download;
    default: return Coins;
  }
}

/** A human title + signed-amount chips for one ledger row. */
function describeEntry(e: LedgerEntry): { title: string; chips: string[]; kind: 'grant' | 'charge' | 'topup' | 'refund' } {
  const unit = unitOf(e);
  // Grants carry the amount in a per-currency field. Post-merge rows have `wp`; pre-merge rows kept
  // their own `sp`/`mp` pair and are still shown that way.
  const grantChips = e.wp !== null && e.wp !== undefined
    ? [`+${e.wp} WP`]
    : [`+${e.sp ?? 0} SP`, `+${e.mp ?? 0} MP`];
  if (e.type === 'welcome') return { title: 'Welcome grant', chips: grantChips, kind: 'grant' };
  if (e.type === 'grant') return { title: 'Monthly refresh', chips: grantChips.filter((c) => !c.startsWith('+0 ')), kind: 'grant' };
  if (e.type === 'topup') return { title: `Top-up${e.pack ? ` · ${e.pack}` : ''}`, chips: [`+${e.amount ?? 0} ${unit}`], kind: 'topup' };
  if (e.type === 'refund') return { title: 'Refund', chips: [`+${e.amount ?? 0} ${unit}`], kind: 'refund' };
  const label = capabilityLabel(e.capability, e.currency);
  return { title: label, chips: [`${e.amount ?? 0} ${unit}`], kind: 'charge' };
}

/** Subtitle: who actioned it (you vs a proactive agent) + the chat it happened in. */
function ActorLine({ entry }: { entry: LedgerEntry }) {
  const a = entry.actor;
  if (!a) return null;
  const isAgent = a.type === 'agent';
  const Icon = isAgent ? Bot : User;
  const who = isAgent ? a.name : 'You';
  const where = a.context ? ` · ${a.context}` : '';
  return (
    <span className="flex items-center gap-1 text-[11px] text-secondary-400 truncate">
      <Icon className="w-3 h-3 shrink-0" />
      <span className="truncate">{who}{where}</span>
    </span>
  );
}

/**
 * Renders wallet ledger rows (grants, charges, top-ups). Shared by the wallet preview + history page.
 * `timezone` (IANA, default UTC) governs how the full date+time+region timestamp is displayed.
 */
const LedgerList: React.FC<{ entries: LedgerEntry[]; emptyHint?: string; timezone?: string | null }> = ({ entries, emptyHint, timezone }) => {
  if (!entries.length) {
    return <p className="mt-2 text-sm text-secondary-400">{emptyHint || 'No transactions yet — your grants, spends, and top-ups will show here.'}</p>;
  }
  return (
    <ul className="mt-3 divide-y divide-secondary-100 dark:divide-secondary-800 border-y border-secondary-100 dark:border-secondary-800">
      {entries.map((e) => {
        const d = describeEntry(e);
        const Icon = iconFor(e);
        return (
          <li key={e.id} data-testid={`ledger-${e.id}`} className="flex items-center gap-3 py-2.5">
            <span className="w-7 h-7 shrink-0 rounded-full bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center">
              <Icon className="w-3.5 h-3.5 text-secondary-500" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm text-secondary-800 dark:text-secondary-100 truncate">{d.title}</span>
              <ActorLine entry={e} />
            </span>
            <span className="flex items-center gap-1.5 shrink-0">
              {d.chips.map((c, i) => (
                <span key={i} className={`text-xs font-medium tabular-nums ${c.startsWith('-') || c.startsWith('−') ? 'text-secondary-500' : 'text-secondary-900 dark:text-white'}`}>{c}</span>
              ))}
            </span>
            <span className="w-36 shrink-0 text-right text-[11px] text-secondary-400 tabular-nums">{formatInstant(e.created_at, timezone)}</span>
          </li>
        );
      })}
    </ul>
  );
};

export default LedgerList;
